import { Grid } from '@atgfr8/atg-ui-tailwind';
import {
  Button,
  Notification,
  Popover,
  TabsComponent,
  usePopoverRef,
} from '@components';
import { FeatureFlags } from '@constants/featureFlags';
import { useParams } from '@solidjs/router';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { closeModal, openModal } from '@store/modals';
import {
  GetCarrierVerificationLockdownValue,
  TabType,
  addNewVendorToOrder,
  carrierStore,
  featureFlagGetCustomerFeatures,
  getCarrierDetail,
  orderStore,
  setActiveCarrierTab,
} from '@store/orders';
import {
  defaultVendorContractXrefViewModel,
  vendorInitialState,
} from '@store/orders/defaultVals';
import { PlusIcon } from '@atgfr8/icons';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import {
  AddCarrierModalId,
  CarrierLoadingCards,
} from '@views/order/components';
import AddNewCarrier from '@views/order/components/models/AddNewCarrier';
import { getEldAssetList } from '@views/order/helpers';
import {
  JSX,
  Show,
  createEffect,
  createSignal,
  onCleanup,
  onMount,
} from 'solid-js';

import ActiveTabContent from './ActiveTabContent';
import { CarrierTab } from './CarrierTab';
import classes from './classes';
import { MobileLoadSelect } from './MobileLoadSelect';
import { PointOfSale } from './PointOfSale';
import { VendorTab } from './VendorTab';
type CarrierProps = {
  lgBreakpoint: number;
};

export const Carrier = (props: CarrierProps) => {
  const [carrierTabs, setCarrierTabs] = createSignal<JSX.Element[]>([]);
  const [vendorTabs, setVendorTabs] = createSignal<JSX.Element[]>([]);
  // vendor array indexes in the order their tabs are displayed
  const [vendorTabOrder, setVendorTabOrder] = createSignal<number[]>([]);
  const [tabIndex, setTabIndex] = createSignal(0);
  const params = useParams();
  const [setAddPopoverRef, closeAddPopover] = usePopoverRef();

  const isCreate = !params.id;

  createEffect(async () => {
    if (
      featureFlagGetCustomerFeatures.featureFlagCustomer
        .TruckerToolsEldAssetApi &&
      carrierStore.carrier[orderStore.activeTab.index]?.mc
    ) {
      await getEldAssetList(
        carrierStore.carrier[orderStore.activeTab.index]?.mc,
      );
    }
  });

  onMount(async () => {
    await GetCarrierVerificationLockdownValue();
  });

  createEffect(() => {
    if (orderStore.order.loads.length === 0) {
      setCarrierTabs([]);
    }

    if (orderStore.order.loads.length > 0) {
      setCarrierTabs(
        orderStore.order.loads.map((item, index) =>
          CarrierTab(index, item, setTabIndex, orderStore.isReadOnly),
        ),
      );
    }

    if (orderStore.order.vendors) {
      if (orderStore.order.vendors.length === 0) {
        setVendorTabs([]);
        setVendorTabOrder([]);
      }

      if (orderStore.order.vendors.length > 0) {
        const orderedVendors = orderStore.order.vendors
          .map((item, index) => ({ item, index }))
          .sort(
            (a, b) =>
              Number(a.item.status === 'Void') -
              Number(b.item.status === 'Void'),
          );

        setVendorTabOrder(orderedVendors.map(({ index }) => index));
        setVendorTabs(
          orderedVendors.map(({ item, index }) =>
            VendorTab(index, item, setTabIndex),
          ),
        );
      }
    }
  });

  createEffect(() => {
    //Note: add this to set the active tab when the page is refreshed
    //eslint-disable-next-line
    if (orderStore.activeTab) {
      const { type, index } = orderStore.activeTab;
      if (type === 'load') {
        setTabIndex(index);
      } else {
        const position = vendorTabOrder().indexOf(index);
        setTabIndex(
          (position === -1 ? index : position) + carrierTabs().length,
        );
      }
    }
  });

  const closeAddNewCarrier = () => {
    closeModal(AddCarrierModalId);
  };

  const handleNewCarrierTab = () => {
    setCarrierTabs([
      ...carrierTabs(),
      CarrierTab(orderStore.order.loads.length),
    ]);
    setActiveCarrierTab({
      index: orderStore.order.loads.length,
      type: TabType.load,
    });
    setTabIndex(orderStore.order.loads.length);
  };

  const handleNewVendorTab = () => {
    addNewVendorToOrder({
      ...vendorInitialState,
      vendorContractXref: isFeatureFlagEnabled(FeatureFlags.VendorManagement)()
        ? { ...defaultVendorContractXrefViewModel }
        : null,
    });
    const vendorIndex = Math.max(
      (orderStore.order.vendors?.length ?? 1) - 1,
      0,
    );
    const position = vendorTabOrder().indexOf(vendorIndex);
    setActiveCarrierTab({
      index: vendorIndex,
      type: TabType.vendor,
    });
    setTabIndex(carrierTabs().length + Math.max(position, 0));
  };

  const handleTabClick = async (index: number) => {
    if (index < carrierTabs().length) {
      setActiveCarrierTab({
        index: index,
        type: TabType.load,
      });
      const carrierId = orderStore.order.loads[index].carrierId;
      if (carrierId !== 0 && carrierId != null) {
        if (typeof carrierStore.carrier[index] === 'undefined') {
          await getCarrierDetail(carrierId, index);
        }
      }
      setTabIndex(index);
    } else {
      const position = index - carrierTabs().length;
      setActiveCarrierTab({
        index: vendorTabOrder()[position] ?? position,
        type: TabType.vendor,
      });
      setTabIndex(index);
    }
  };

  onCleanup(() => {
    closeAddNewCarrier();
  });

  return (
    <Grid item size={{ md: 12, lg: props.lgBreakpoint }}>
      <Show when={!orderStore.isMobile} fallback={<MobileLoadSelect />}>
        <PointOfSale />
        <TabsComponent
          id="order-load-tabs"
          activeTab={tabIndex()}
          onTabClick={handleTabClick}
          tabs={[...carrierTabs(), ...vendorTabs()]}
          tabStyleClasses="rounded-t-lg"
          tabContainerStyles="ml-4 items-center"
          activeStyling="bg-[#ffe8b7]"
          border={true}
          noPadding={true}
          showChevrons={true}
          loading={orderStore.loading}
          actionButton={
            <Show when={!orderStore.loading}>
              <Popover
                ref={setAddPopoverRef}
                eleName={
                  <PlusIcon class="cursor-pointer rounded-full border-2 border-[#468DB5] text-[#468DB5]" />
                }
              >
                <div>
                  <Button
                    class={classes.carrierOptions}
                    size="md"
                    onClick={() => {
                      openModal(AddCarrierModalId);
                      closeAddPopover();
                    }}
                    label="Carrier"
                    variant="text"
                    disabled={orderStore.isReadOnly}
                    dataTestId={formatDataTestId('Carrier', 'button')}
                  />
                  <br />
                  <Button
                    class={classes.carrierOptions}
                    onClick={() => {
                      handleNewVendorTab();
                      closeAddPopover();
                    }}
                    label="Vendor"
                    size="md"
                    variant="text"
                    disabled={orderStore.isReadOnly}
                    dataTestId={formatDataTestId('Vendor', 'button')}
                  />
                </div>
              </Popover>
            </Show>
          }
        />
      </Show>
      <div class="mobile:hidden nonMobile:block border-t border-gray-300 mb-2" />
      <Show
        when={
          orderStore.orderFormError &&
          orderStore.orderFormError[
            `loads[${orderStore.activeTab.index}].stopsCustomError`
          ] &&
          orderStore.activeTab.type === TabType.load
        }
      >
        <div class="mb-2">
          {orderStore.orderFormError &&
            orderStore.orderFormError[
              `loads[${orderStore.activeTab.index}].stopsCustomError`
            ]?.map((error) => <Notification type="error" text={error} />)}
        </div>
      </Show>
      <Show when={!orderStore.loading} fallback={<CarrierLoadingCards />}>
        <div>
          <ActiveTabContent />
        </div>
      </Show>
      <AddNewCarrier
        onClose={closeAddNewCarrier}
        onSubmit={handleNewCarrierTab}
      />
      <Show
        when={
          isCreate && carrierTabs().length === 0 && vendorTabs().length === 0
        }
      >
        <div class="text-center mt-2">
          Start by adding a{' '}
          <Button
            label="Carrier"
            variant="text"
            onClick={() => openModal(AddCarrierModalId)}
            dataTestId={formatDataTestId('Carrier', 'button')}
          />{' '}
        </div>
      </Show>
    </Grid>
  );
};
